import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Stack,
  VStack,
  Image,
  useColorModeValue,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { fadeInUp, slideInLeft, slideInRight } from '@/utils/animation';
import { PERSONAL_INFO } from '@/constants/data';

import { useEffect } from 'react';
import { Link } from 'react-scroll';

const MotionBox = motion(Box);
const MotionStack = motion(Stack);
const MotionImage = motion(Image);

const OrbitingParticle = ({ delay = 0, size = 4, color = 'brand.400' }) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      rotate: 360,
      transition: {
        duration: 20,
        repeat: Infinity,
        ease: 'linear',
        delay,
      },
    });
  }, [controls, delay]);

  return (
    <MotionBox
      position="absolute"
      animate={controls}
      style={{ transformOrigin: 'center' }}
    >
      <Box
        position="absolute"
        w={`${size}px`}
        h={`${size}px`}
        bg={color}
        borderRadius="full"
        transform={`translate(-50%, -50%) translateX(${120 + Math.random() * 80}px)`}
      />
    </MotionBox>
  );
};

const BouncingBall = ({ delay = 0 }) => {
  return (
    <MotionBox
      position="absolute"
      bottom="20px"
      left={`${Math.random() * 100}%`}
      w="8px"
      h="8px"
      bg="brand.400"
      borderRadius="full"
      animate={{
        y: [-20, 0],
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        delay,
        times: [0, 0.5, 1],
      }}
    />
  );
};

export default function Banner() {
  const bgGradient = useColorModeValue(
    'linear(to-r, brand.50, highlight.50, accent.50)',
    'linear(to-r, brand.900, highlight.900, accent.900)'
  );
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const highlightColor = useColorModeValue('brand.500', 'brand.400');
  const particleColor = useColorModeValue('brand.400', 'brand.200');
  const buttonBg = useColorModeValue('brand.500', 'brand.400');
  const buttonHoverBg = useColorModeValue('brand.600', 'brand.500');
  const socialIconColor = useColorModeValue('brand.500', 'brand.400');
  const socialIconHoverColor = useColorModeValue('accent.500', 'accent.400');

  return (
    <Box
      as="section"
      minH="80vh"
      pt="5rem"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
      id="home"
    >
      <Container maxW="7xl" position="relative" overflow="hidden">
        {/* Orbit animation container */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="100%"
          h="100%"
          maxW="1200px"
          maxH="400px"
          opacity={0.6}
        >
          {/* Orbiting particles */}
          {Array.from({ length: 24 }).map((_, i) => (
            <OrbitingParticle
              key={i}
              delay={i * 0.8}
              size={2 + Math.random() * 3}
              color="brand.400"
            />
          ))}
        </Box>

        {/* Bouncing balls */}
        {Array.from({ length: 8 }).map((_, i) => (
          <BouncingBall key={i} delay={i * 0.4} />
        ))}
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: 10, lg: 20 }}
          align="center"
          justify="space-between"
        >
          <MotionStack
            flex={1}
            spacing={4}
            variants={slideInLeft}
            initial="initial"
            animate="animate"
          >
            <Text
              color={highlightColor}
              fontWeight="semibold"
              fontSize="lg"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Welcome to my portfolio
            </Text>

            <Heading
              as="h1"
              fontSize={{ base: '3xl', sm: '4xl', md: '4xl', lg: '5xl' }}
              lineHeight="shorter"
              fontWeight="bold"
              letterSpacing="tight"
            >
              {PERSONAL_INFO.name}
            </Heading>

            <Box h="50px">
              <TypeAnimation
                sequence={[
                  'Full Stack Developer',
                  2000,
                  'Web3 Engineer',
                  2000,
                  'AI Developer',
                  2000,
                ]}
                wrapper="span"
                speed={50}
                style={{
                  fontSize: '1.5em',
                  display: 'inline-block',
                  color: 'var(--chakra-colors-brand-500)',
                  fontWeight: 'bold',
                }}
                repeat={Infinity}
              />
            </Box>

            <VStack spacing={4} align="start" maxW="2xl">
              <Text fontSize="lg" color={textColor} lineHeight="tall">
                Versatile Full Stack Developer with expertise in Web2, Web3, and AI technologies. Specializing in building secure DeFi protocols, NFT marketplaces, and AI-powered web applications. Passionate about bridging traditional web development with blockchain innovation and artificial intelligence.
              </Text>

              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                align={{ base: 'stretch', sm: 'center' }}
                pt={4}
              >
                <Link to="contact" smooth={true} offset={-70} duration={500}>
                  <Button
                    size="lg"
                    bg={buttonBg}
                    color="white"
                    px={8}
                    fontSize="md"
                    fontWeight="bold"
                    _hover={{
                      bg: buttonHoverBg,
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    transition="all 0.3s ease-in-out"
                  >
                    Get in Touch
                  </Button>
                </Link>
                <Link to="projects" smooth={true} offset={-70} duration={500}>
                  <Button
                    size="lg"
                    variant="outline"
                    borderColor={buttonBg}
                    color={buttonBg}
                    px={8}
                    fontSize="md"
                    fontWeight="bold"
                    _hover={{
                      bg: 'rgba(0, 163, 255, 0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    transition="all 0.3s ease-in-out"
                  >
                    View Projects
                  </Button>
                </Link>
              </Stack>
            </VStack>
          </MotionStack>

          <MotionBox
            flex={1}
            variants={slideInRight}
            initial="initial"
            animate="animate"
            position="relative"
          >
            <Box
              position="relative"
              w="300px"
              h="300px"
              mx="auto"
              borderRadius="full"
              overflow="hidden"
              border="4px solid"
              borderColor={useColorModeValue('green.500', 'green.200')}
              boxShadow="xl"
            >
              <Image
                src="/profileImage.jpg"
                alt="Danish Ismail"
                w="full"
                h="full"
                objectFit="cover"
                fallback={<Box w="full" h="full" bg="gray.200" />}
              />
            </Box>
          </MotionBox>
        </Stack>

        <MotionBox
          position="absolute"
          bottom="5rem"
          left="50%"
          transform="translateX(-50%)"
          variants={fadeInUp}
          initial="initial"
          animate="animate"
        >
          <Link to="services" smooth={true} offset={-70} duration={500}>
            <motion.div
              animate={{
                y: [0, 10, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
              style={{ cursor: 'pointer' }}
            >
              <Text
                fontSize="sm"
                color={textColor}
                textAlign="center"
                mb={2}
              >
                Scroll Down
              </Text>
              <Box
                w="2px"
                h="50px"
                bg={highlightColor}
                mx="auto"
                borderRadius="full"
              />
            </motion.div>
          </Link>
        </MotionBox>
      </Container>
    </Box>
  );
}
